import { motion } from "framer-motion";
import {
  HandHeart,
  Smiley,
  Sparkle,
  ThumbsUp,
  Leaf,
  Crown,
  ShieldCheck,
  Heart,
  ArrowRight,
  Palette,
} from "@phosphor-icons/react";
import { Mascot } from "../Mascot";
import { BloomButton } from "../ui/BloomButton";

const PRINCIPLES = [
  { icon: HandHeart, label: "Warm" },
  { icon: Smiley, label: "Friendly" },
  { icon: Sparkle, label: "Delightful" },
  { icon: ThumbsUp, label: "Encouraging" },
  { icon: Leaf, label: "Calm" },
  { icon: Crown, label: "Premium" },
  { icon: ShieldCheck, label: "Safe" },
  { icon: Heart, label: "Accessible" },
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export function HeroSection() {
  return (
    <section id="overview" className="relative overflow-hidden scroll-mt-24">
      {/* decorative glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute right-0 top-20 h-64 w-64 rounded-full bg-teal/15 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-60 w-60 rounded-full bg-secondary/20 blur-3xl" />
      </div>

      <div className="relative mx-auto grid w-full max-w-content items-center gap-12 px-5 py-16 sm:px-8 md:py-24 lg:grid-cols-[1.15fr_0.85fr] lg:px-12">
        <div>
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-sm font-bold uppercase tracking-wider text-accent-foreground"
          >
            <Sparkle size={16} weight="fill" />
            Kids Portal · Years 1–6
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 font-display text-4xl font-black leading-[1.05] tracking-tight text-balance md:text-6xl"
          >
            A calm, joyful place
            <br />
            to <span className="text-primary-strong">learn &amp; grow.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-6 max-w-xl text-lg font-medium leading-relaxed text-muted-foreground md:text-xl"
          >
            This is the design foundation for <strong className="text-foreground">Bloom</strong> — warm, friendly
            and accessible. One consistent language that grows gracefully from Year 1 to Year 6 and scales to 50+ screens.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <BloomButton size="lg" rightIcon={ArrowRight} onClick={() => scrollTo("colour")} data-testid="hero-explore-btn">
              Explore the palette
            </BloomButton>
            <BloomButton size="lg" variant="outline" leftIcon={Palette} onClick={() => scrollTo("components")} data-testid="hero-components-btn">
              View components
            </BloomButton>
          </motion.div>

          <div className="mt-10 flex flex-wrap gap-2.5">
            {PRINCIPLES.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-2 text-sm font-bold text-foreground shadow-soft"
              >
                <Icon size={16} weight="fill" className="text-primary" />
                {label}
              </span>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1], delay: 0.1 }}
          className="relative mx-auto w-full max-w-sm"
        >
          <div className="relative rounded-[2.5rem] border border-border bg-card p-10 shadow-soft-lg">
            <div className="absolute right-6 top-6 rounded-full bg-secondary/25 px-3 py-1.5 text-sm font-bold text-secondary-ink">
              Hello! I&apos;m Bloom
            </div>
            <div className="flex items-center justify-center py-6">
              <Mascot size={200} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl bg-primary/12 p-3 text-center">
                <p className="font-display text-xl font-extrabold text-primary-strong">A+</p>
                <p className="text-xs font-bold text-muted-foreground">Friendly</p>
              </div>
              <div className="rounded-2xl bg-teal/15 p-3 text-center">
                <p className="font-display text-xl font-extrabold text-teal-ink">AA</p>
                <p className="text-xs font-bold text-muted-foreground">Accessible</p>
              </div>
              <div className="rounded-2xl bg-blue/12 p-3 text-center">
                <p className="font-display text-xl font-extrabold text-blue-ink">50+</p>
                <p className="text-xs font-bold text-muted-foreground">Screens</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default HeroSection;
